import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {errorListPush} from "../state/action/alertActions";
import '../style/MACAddressInput.css';
import Icon, {BACKSPACE, CHECK_MARK} from "./Icon";

export const INPUT_TYPE = Object.freeze({
    keyboardInput: 'keyboardInput',
    scannerInput: 'scannerInput',
    noInput: 'noInput'
});

export const initialState = Object.freeze({
    inputSelection: INPUT_TYPE.noInput,
    octetSelection: -1,
    scannerKey: 1
});

// Currently expects MAC addresses with separators '-' or ':'
export const MAC_REGEX = '^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$';

export class MACAddressInput extends Component {

    state = initialState;

    componentDidMount() {
        if (this.props.editable) {
            this.handleSelectKeyboard(undefined);
        }
    }

    static getDerivedStateFromProps(props, state) {
        if (props.editable === false) {
            return {inputSelection: initialState.inputSelection, octetSelection: initialState.octetSelection};
        } else {
            return null;
        }
    }

    /**
     * Returns the MAC address split in to 2 byte array (eg "112233..." -> ["11", "22"...]
     */
    getMac = () => {
        return this.props.mac.match(/.{1,2}/g);
    };

    renderKeyboard() {
        return (
            <div className={'mac-keyboard'}>
                {this.renderKeyboardInput(
                    <Icon iconName={CHECK_MARK} height={'3em'} width={'3em'}/>,
                    6, 1, {
                        backgroundColor: 'lightgreen',
                        icon: true,
                        callback: this.handleKeyboardOK
                    })}
                {this.renderKeyboardInput(0, 6, 2)}
                {this.renderKeyboardInput(
                    <Icon iconName={BACKSPACE} height={'2em'} width={'2em'}/>,
                    6, 3,
                    {
                        backgroundColor: 'lightcoral',
                        icon: true,
                        callback: this.clearOctet
                    })}
                {this.renderKeyboardInput(1, 5, 1)}
                {this.renderKeyboardInput(2, 5, 2)}
                {this.renderKeyboardInput(3, 5, 3)}
                {this.renderKeyboardInput(4, 4, 1)}
                {this.renderKeyboardInput(5, 4, 2)}
                {this.renderKeyboardInput(6, 4, 3)}
                {this.renderKeyboardInput(7, 3, 1)}
                {this.renderKeyboardInput(8, 3, 2)}
                {this.renderKeyboardInput(9, 3, 3)}
                {this.renderKeyboardInput('A', 2, 1)}
                {this.renderKeyboardInput('B', 2, 2)}
                {this.renderKeyboardInput('C', 2, 3)}
                {this.renderKeyboardInput('D', 1, 1)}
                {this.renderKeyboardInput('E', 1, 2)}
                {this.renderKeyboardInput('F', 1, 3)}
            </div>
        );
    }

    renderKeyboardInput = (char, row, col, options) => {
        let callback = (options && options.callback) ? options.callback : () => this.assignKeyboardInput(char);
        return (
            <div
                className={'mac-keyboard-row'}
                style={{
                    gridColumn: col + ' / span 1',
                    gridRow: row + ' / span 1',
                    backgroundColor: (options && options.backgroundColor) ? options.backgroundColor : undefined,
                    fontSize: (options && options.icon) ? 'unset' : undefined
                }}
                id={char.length === 1 ? 'mac-keyboard-' + char : undefined}
                onClick={callback}
            >{char}</div>
        );
    };

    handleKeyboardOK = () => {
        this.setState({
            inputSelection: initialState.inputSelection,
            octetSelection: initialState.octetSelection
        });
        this.props.complete && this.props.complete();
    };

    clearOctet = () => {
        if (this.getMac()[this.state.octetSelection] !== '__') {
            this.props.submitEdit({
                mac: MACAddressInput.replaceOctet(
                    this.getMac(),
                    this.state.octetSelection,
                    '__'
                ).join('')
            });
        } else if (this.state.octetSelection > 0) {
            this.props.submitEdit({
                mac: MACAddressInput.replaceOctet(
                    this.getMac(),
                    this.state.octetSelection - 1,
                    '__'
                ).join('')
            });
            this.setState({octetSelection: this.state.octetSelection - 1});
        }
    };

    assignKeyboardInput = (char) => {
        if (MACAddressInput.isValidOctet(this.state.octetSelection)) {
            if (this.getMac()[this.state.octetSelection].split('')[0] === '_') {
                this.props.submitEdit({
                    mac: MACAddressInput.replaceOctet(
                        this.getMac(),
                        this.state.octetSelection,
                        char + '_'
                    ).join('')
                });
            } else if (this.getMac()[this.state.octetSelection].split('')[1] === '_') {
                this.props.submitEdit({
                    mac: MACAddressInput.replaceOctet(
                        this.getMac(),
                        this.state.octetSelection,
                        this.getMac()[this.state.octetSelection].split('')[0] + char
                    ).join('')
                });
                this.setState({octetSelection: this.state.octetSelection + 1});
            } else {
                this.props.submitEdit({
                    mac: MACAddressInput.replaceOctet(
                        this.getMac(),
                        this.state.octetSelection,
                        char + '_'
                    ).join('')
                });
            }
        }
    };

    getMacDisplay = () => {
        const mac = this.getMac();
        return (
            <React.Fragment>
                {this.renderOctetDisplay(0, mac[0])}
                {this.renderOctetSeparator()}
                {this.renderOctetDisplay(1, mac[1])}
                {this.renderOctetSeparator()}
                {this.renderOctetDisplay(2, mac[2])}
                {this.renderOctetSeparator()}
                {this.renderOctetDisplay(3, mac[3])}
                {this.renderOctetSeparator()}
                {this.renderOctetDisplay(4, mac[4])}
                {this.renderOctetSeparator()}
                {this.renderOctetDisplay(5, mac[5])}
            </React.Fragment>
        );
    };

    renderOctetSeparator() {
        return <div>:</div>;
    };

    renderOctetDisplay = (ordinal, value) => {
        return (
            <div
                className={'mac-address-octet-display'}
                style={{
                    border: ordinal === this.state.octetSelection ? 'solid 1px black' : 'solid 1px white'
                }}
                onClick={this.props.editable ? (() => this.handleSelectKeyboard(ordinal)) : undefined}
            >{value.toUpperCase()}</div>
        );
    };

    static replaceOctet(oldMac, octet, newValue) {
        if (this.isValidOctet(octet)) {
            let newMac = [...oldMac];
            newMac[octet] = newValue;
            return newMac;
        } else {
            return oldMac;
        }
    };

    static isValidOctet(ordinal) {
        return ordinal >= 0 && ordinal <= 5;
    }

    handleSelectKeyboard = (ordinal) => {
        let nextOctet = ordinal === undefined ? this.getMac().findIndex(octet => {
            return octet.includes('_');
        }) : ordinal;
        this.setState({inputSelection: INPUT_TYPE.keyboardInput, octetSelection: nextOctet});
    };

    render() {
        return (
            <div className={'mac-wrapper'}>
                <div className={'mac-display'}>
                    {this.getMacDisplay()}
                </div>
                {
                    this.state.inputSelection === INPUT_TYPE.keyboardInput &&
                    this.renderKeyboard()
                }
            </div>
        );
    }
}

MACAddressInput.propTypes = {
    mac: PropTypes.string.isRequired,
    submitEdit: PropTypes.func,
    editable: PropTypes.bool,
    complete: PropTypes.func
};

const mapDispatchToProps = (dispatch) => {
    return {
        errorPush: (error) => {
            dispatch(errorListPush(error));
        }
    };
};

export default connect(null, mapDispatchToProps)(MACAddressInput);


// WEBPACK FOOTER //
// ./src/component/MACAddressInput.jsx